import * as React from 'react'
import filter from 'lodash/filter'
import includes from 'lodash/includes'
import {useParams} from 'react-router'
import {fromJust, mmap} from '@freckle/maybe'
import {exhaustive} from '@freckle/exhaustive'
import {type NonEmptyArray, mapOnNonEmpty} from '@freckle/non-empty'
import {type ApiErrorT, responseApiErrors} from '@frontrow/console/src/entities/models/api-error'

import {fetchDistrictsRgpCsvs} from '@frontrow/console/src/helpers/api-districts'
import {
  parseDistrictRosterCsvUrls,
  type DistrictRosterCsvUrlsT
} from '@frontrow/console/src/models/district-rosters-csv-urls'
import {DistrictsRgpCsvs} from '@frontrow/console/src/components/pages/districts-rgp-csv-page/districts-rgp-csv-page'

type State =
  | {
      type: 'loading'
    }
  | {
      type: 'loaded'
      data: Array<DistrictRosterCsvUrlsT>
    }
  | {
      type: 'error'
      errors: NonEmptyArray<ApiErrorT>
    }

export function DistrictsRgpCsvsContainer(): React.ReactElement {
  const params = useParams() as Record<string, string>
  const {districtId: mDistrictId, schoolId: mSchoolId} = params
  const districtId = parseInt(fromJust(mDistrictId, 'No District ID'), 10)
  const schoolId = mmap(schoolId => parseInt(schoolId, 10), mSchoolId)

  const [state, setState] = React.useState<State>({type: 'loading'})

  React.useEffect(() => {
    fetchDistrictsRgpCsvs(districtId)
      .then(parseDistrictRosterCsvUrls)
      .then(data => {
        if (schoolId !== null && schoolId !== undefined) {
          return filter(data, x => includes(x.tenantFreckleSchoolIds, schoolId))
        } else {
          return data
        }
      })
      .then(
        data => setState({type: 'loaded', data}),
        xhr => setState({type: 'error', errors: responseApiErrors(xhr)})
      )
  }, [districtId, schoolId])

  switch (state.type) {
    case 'loaded':
      return <DistrictsRgpCsvs csvUrls={state.data} />
    case 'error':
      const errorNodes = mapOnNonEmpty(state.errors, ({name, message, details}: ApiErrorT) => (
        <li>
          {name}: {message} {details && <pre>{JSON.stringify(details)}</pre>}
        </li>
      ))
      return <ul>{errorNodes}</ul>
    case 'loading':
      return <p>loading...</p>
    default:
      return exhaustive(state as never)
  }
}
